import { inject, Injectable } from '@angular/core';
import { type Observable, of } from 'rxjs';

import { AccountsTableStaleness } from '@blaze-runtime/engine-data-access';
import { RuntimeErrorMessage } from '@blaze-runtime/engine-domain';
import {
  type DatasetUpdateRequest,
  type LogicalPredicateNode,
} from '@blaze-shared/api-contracts-domain';
import { type RuntimeContext } from '@blaze-shared/elements-domain';
import { IntelliSenseService } from '@blaze-shared/intellisense-feature';
import { NotificationService } from '@blaze-shared/platform-data-access';
import { DatasetRowsService, SchemaService } from '@blaze-shared/schema-data-access';
import { type DatasetRow } from '@blaze-shared/schema-domain';
import { isNullishOrEmptyString } from '@blaze-shared/shared-util';
import { type UpdateWorkflowNode } from '@blaze-shared/workflow-domain';

import { getRowId } from '../../dataset-row-id';
import { NodeResultMessagesService } from '../../node-result-messages.service';

/** Which rows an update targets: the one its context identifies, or whatever its query matches. */
interface UpdateTarget {
  queryNode?: LogicalPredicateNode;
  rowId?: string;
}

/**
 * Updates dataset rows from the node's assignments.
 *
 * A list of input rows updates once per row; a single object updates once. A bulk-edit node is the
 * exception: its input rows already hold the values to write, so they are sent as they are instead
 * of being rebuilt from assignments.
 */
@Injectable({ providedIn: 'root' })
export class DatasetUpdateActionService {
  private readonly accountsTableStaleness = inject(AccountsTableStaleness);
  private readonly datasetRowsService = inject(DatasetRowsService);
  private readonly iss = inject(IntelliSenseService);
  private readonly nodeResultMessages = inject(NodeResultMessagesService);
  private readonly notificationService = inject(NotificationService);
  private readonly schemaService = inject(SchemaService);

  datasetUpdate(
    node: UpdateWorkflowNode,
    nodeInput: any,
    ctx: RuntimeContext,
    workflowId?: string,
    workflowExecutionId?: string,
  ): Observable<any> {
    if (!node.datasetId) {
      return of([]);
    }
    const { errorMessage, successMessage } = this.nodeResultMessages.getErrorAndSuccessMessage(
      node,
      nodeInput,
      ctx,
    );

    if (Array.isArray(nodeInput) && nodeInput.length) {
      const updateRequests: DatasetUpdateRequest[] = nodeInput.map((row) => {
        const context = this.iss.updateRuntimeContext(ctx, row);
        return {
          ...this.resolveTarget(node, context),
          upsert: !!node.upsert,
          value: node.bulkEdit ? row : this.buildRowUpdate(node, context),
          workflowExecutionId,
          workflowId,
        };
      });
      this.markAccountsStaleWhenWritingAccounts(node);
      return this.datasetRowsService.updateBatch(
        node.datasetId,
        updateRequests,
        successMessage,
        errorMessage,
        undefined,
        true,
      );
    }
    if (Array.isArray(nodeInput) && !nodeInput.length) {
      return of([]);
    }

    const context = this.iss.updateRuntimeContext(ctx, nodeInput);
    const { queryNode, rowId } = this.resolveTarget(node, context);
    const rowUpdate: DatasetRow = {};
    node.assignments.forEach(({ name, value }) => {
      rowUpdate[this.schemaService.getColumnName(name) || ''] = this.iss.eval(value, context);
    });
    this.markAccountsStaleWhenWritingAccounts(node);
    return this.datasetRowsService.update(
      node.datasetId,
      { queryNode, rowId: rowId ?? '', upsert: node.upsert, value: rowUpdate },
      { errorMsg: errorMessage, showThemedNotification: true, successMsg: successMessage },
    );
  }

  /**
   * @throws when a current-row node's context carries no row id and the node cannot upsert — there
   * is no row to update and none may be created.
   */
  private resolveTarget(node: UpdateWorkflowNode, context: RuntimeContext): UpdateTarget {
    if (!node.currentRow) {
      return {
        queryNode: this.iss.createDatasetQuery(
          node.query,
          node.queryConditions,
          node.condition || '',
          node.queryLogicalOp,
          context,
        ),
      };
    }
    const idCol = this.schemaService.getIdColumnName(node.datasetId);
    const rowId = getRowId(idCol, context);
    if (isNullishOrEmptyString(rowId) && !node.upsert) {
      console.error(`Row ID not found in row: ${rowId}, ID column: ${idCol}`);
      throw new Error(RuntimeErrorMessage.MissingRowInfo);
    }
    return { rowId };
  }

  /** Builds the update, dropping columns the dataset does not allow workflows to write. */
  private buildRowUpdate(node: UpdateWorkflowNode, context: RuntimeContext): DatasetRow {
    const rowUpdate: DatasetRow = {};
    node.assignments.forEach(({ name, value }) => {
      if (this.schemaService.isColumnRestricted(name, node.datasetId)) {
        const errorMsg = `Unable to update restricted column ${name}. Removing it from the query.`;
        this.notificationService.error(errorMsg, true);
        console.error(errorMsg);
        return;
      }
      rowUpdate[this.schemaService.getColumnName(name) || ''] = this.iss.eval(value, context);
    });
    return rowUpdate;
  }

  private markAccountsStaleWhenWritingAccounts(node: UpdateWorkflowNode): void {
    this.accountsTableStaleness.markStale(this.schemaService.isAccountsTable(node.datasetId ?? ''));
  }
}
